import React from "react";
import type { MyEffectRow } from "../types/cardTypes";
import RespectCard from "./RespectCard";
import CardStatusChip from "./CardStatusChip";
import { useNowTicker } from "../hooks/useNowTicker";
import { formatRemaining, formatWallClockEnd, msUntil } from "../lib/expiry";
import "../styles/cards.css";

export interface CardRowProps {
  effect: MyEffectRow;
}

/**
 * List row for a single live owner effect (my-effects v2). Shows the card
 * face, the effect state (Active / Armed), and an honest "when it ends"
 * readout: charges as "N/total", anchored windows as an armed-at label,
 * fixed windows as a wall-clock end, until-event / end-of-day as the lifetime
 * label.
 */
export default function CardRow({ effect }: CardRowProps) {
  const now = useNowTicker(30_000);
  const def = effect.definition;
  const kind = effect.lifetime?.kind;

  const chipKind = effect.state === "pending" ? "armed" : "active";

  const readout = ((): string => {
    if (effect.charges) {
      return `${effect.charges.remaining}/${effect.charges.total} charges left`;
    }
    if (kind === "anchored_window") {
      if (effect.state === "pending") return "Armed — waiting for the race";
      const delta = msUntil(effect.expiresAt, now);
      if (delta !== null && delta > 0) return `Live · ${formatRemaining(delta)} left`;
      return "Window over";
    }
    if (kind === "until_event" || kind === "end_of_day") {
      return effect.lifetime?.label ?? "Active";
    }
    const delta = msUntil(effect.expiresAt, now);
    const end = formatWallClockEnd(effect.expiresAt);
    if (delta !== null && delta > 0 && end) {
      return `Ends ${end} · ${formatRemaining(delta)} left`;
    }
    if (delta !== null && delta > 0) {
      return `${formatRemaining(delta)} left`;
    }
    return effect.lifetime?.label ?? "Active";
  })();

  return (
    <li className="card-row">
      <div className="card-row__face">
        <RespectCard card={def} size="xs" showDetails expandable />
      </div>
      <div className="card-row__body">
        <div className="card-row__head">
          <span className="card-row__name">
            {def?.emoji} {def?.name ?? "Unknown card"}
          </span>
          <CardStatusChip kind={chipKind} />
        </div>
        <p className="card-row__readout">{readout}</p>
        {effect.targetUsername ? (
          <p className="card-row__target">on @{effect.targetUsername}</p>
        ) : null}
      </div>
    </li>
  );
}
